import {
  Typography,
  Box,
  Button,
  Checkbox,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import SickIcon from "@mui/icons-material/Sick";
import React, { useContext, useEffect, useState } from "react";
import { SubPageTitleWithUnderline } from "../components/common/SubPageTitleWithUnderline";
import { DaySelection } from "../components/arbeitsplan/DaySelection";
import {
  AbwesendMitarbeiter,
  ArbeitsplanType,
  PersonIcon,
  selectedDayType,
  Terminarten,
  UserType,
  WorkPlanContainer,
} from "../utils/types";
import {
  getArbeitsplanTitle,
  getArbeitsplanTitleNoVorgang,
  getFullHourTimeFrame,
  getIsAnyDateInRange,
} from "../utils/helperFunctions";
import { PageWrapper } from "../components/common/PageWrapper";
import {
  filterAbwesend,
  filterKurzubersichtBaustellenansicht,
  filterKurzubersichtMitarbeiteransicht,
} from "../components/arbeitsplan/ArbeitsplanFilter";
import { Monteur } from "../components/arbeitsplan/Monteur";
import moment from "moment";
import AuthContext from "../context/AuthContext";

export const KurzubersichtPage = () => {
  const [mitarbeiterAnsicht, setMitarbeiterAnsicht] = useState<{
    [mitarbeiter: string]: {
      user: UserType;
      workplansList: ArbeitsplanType[][];
    };
  }>();
  const [baustellenAnsicht, setBaustellenAnsicht] = useState<
    ArbeitsplanType[][]
  >([]);
  const [abwesend, setAbwesend] = useState<AbwesendMitarbeiter[]>([]);
  const [users, setUsers] = useState<UserType[]>([]);
  const [selectedDays, setSelectedDays] = useState<selectedDayType[]>([]);
  const [showBaustellenAnsicht, setShowBaustellenAnsicht] = useState(true);
  const [lateDialogOpen, setLateDialogOpen] = useState(false);
  const [selectedLateUser, setSelectedLateUser] = useState<UserType | null>(
    null,
  );
  const [lateMinutesValue, setLateMinutesValue] = useState<number>(5);
  const [isEditingLateMinutes, setIsEditingLateMinutes] = useState(false);
  const [sickDialogOpen, setSickDialogOpen] = useState(false);
  const [selectedSickUser, setSelectedSickUser] = useState<UserType | null>(
    null,
  );
  const { user } = useContext(AuthContext);
  const currentUser: UserType = user;

  const isStatusActiveToday = (dateString: Date | undefined | string) => {
    if (!dateString) return false;
    return moment()
      .subtract(4, "hours")
      .isSame(moment(dateString).subtract(4, "hours"), "day");
  };

  useEffect(() => {
    const getAllUsers = async () => {
      const response = await fetch(`/api/getAllUsers/`);
      const data = await response.json();
      setUsers(data);
    };
    getAllUsers();
  }, []);

  const [availableDays, setAvailableDays] = useState<selectedDayType[]>([]);

  useEffect(() => {
    const getArbeitsplan = async () => {
      const response = await fetch(`/api/getArbeitsplan/`, {
        cache: "no-cache",
      });
      const data = await response.json();

      let filteredData: ArbeitsplanType[] = data;
      
      // Anforderung: "Bei Deaktivierung der Tagesfilter sollen alle Nutzer nur die Tage sehen, 
      // die über die Checkboxen zu erreichen sind. Keine weiteren Tage"
      if (selectedDays && selectedDays.length > 0) {
        // Filtern nach explizit ausgewählten Tagen
        filteredData = filteredData.filter((plan: ArbeitsplanType) =>
          selectedDays.some((tag) => getIsAnyDateInRange(plan, tag.days)),
        );
      } else if (availableDays && availableDays.length > 0) {
        // Kein expliziter Filter: Filtern nach allen *verfügbaren* Checkbox-Tagen
        filteredData = filteredData.filter((plan: ArbeitsplanType) =>
          availableDays.some((tag) => getIsAnyDateInRange(plan, tag.days)),
        );
      }

      setBaustellenAnsicht(filterKurzubersichtBaustellenansicht(filteredData));
      setMitarbeiterAnsicht(
        filterKurzubersichtMitarbeiteransicht(filteredData, users),
      );

      setAbwesend(
        filterAbwesend(filteredData, users, [
          Terminarten["Krank"],
          Terminarten["Freizeitausgleich"],
          Terminarten["überbetriebliche Unterweisung"],
          Terminarten["Prüfung"],
          Terminarten["Feiertag"],
          Terminarten["Baustellentermin"],
          Terminarten["Baubesprechung"],
          Terminarten["Besprechungstermin"],
          Terminarten["Arbeitsunfall"],
          Terminarten["Vergabetermin"],
        ]),
      );
    };

    getArbeitsplan();
  }, [selectedDays, availableDays, users, showBaustellenAnsicht]);

  const handleCheckboxChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    userid: string,
  ) => {
    await fetch(`/api/setUserChecked/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userID: userid,
        ischecked: e.target.checked,
      }),
    });
  };

  const handleLateChange = async (
    userid: string,
    is_late: boolean,
    late_minutes: number = 0,
  ) => {
    await fetch(`/api/setUserLate/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userID: userid,
        is_late: is_late,
        late_minutes: late_minutes,
      }),
    });
  };

  const handleSickChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    userid: string,
  ) => {
    await fetch(`/api/setUserSick/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userID: userid,
        is_sick: e.target.checked,
      }),
    });
  };

  return (
    <PageWrapper>
      <SubPageTitleWithUnderline title="Kurzübersicht" />

      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        gap={2}
        marginBottom={2}
      >
        <Box width={{ xs: "100%", sm: "auto" }}>
          <Button
            onClick={() => {
              window.print();
            }}
            sx={{ boxShadow: 3 }}
          >
            Drucken
          </Button>
          <style>
            {`
              @media print {
              body {
                zoom: 0.5; /* Adjust the zoom level as needed */
              }
              }
            `}
          </style>
        </Box>

        <Box>
          {currentUser !== null &&
            (currentUser.bemerkung === "GF" ||
              currentUser.bemerkung === "Personal") && (
              <Button
                onClick={() => setShowBaustellenAnsicht(!showBaustellenAnsicht)}
                sx={{ boxShadow: 3 }}
              >
                {showBaustellenAnsicht
                  ? "Mitarbeiteransicht"
                  : "Baustellenansicht"}
              </Button>
            )}
        </Box>
      </Box>
      <DaySelection
        selectedDays={selectedDays}
        setSelectedDays={setSelectedDays}
        setAvailableDays={setAvailableDays}
        windowSize={window.innerWidth}
        preselectToday={true}
        showWeekNavigation={
          currentUser &&
          (currentUser.bemerkung === "GF" ||
            currentUser.bemerkung === "Personal")
        }
      />

      {!showBaustellenAnsicht &&
        currentUser &&
        (currentUser.bemerkung === "GF" ||
          currentUser.bemerkung === "Personal") &&
        mitarbeiterAnsicht &&
        Object.entries(mitarbeiterAnsicht)
          .sort(([_, a], [__, b]) => {
            const aChecked =
              isStatusActiveToday(a.user?.checked) ||
              isStatusActiveToday(a.user?.sick);
            const bChecked =
              isStatusActiveToday(b.user?.checked) ||
              isStatusActiveToday(b.user?.sick);

            if (aChecked && bChecked) {
              return a.user.last_name.localeCompare(b.user.last_name);
            }
            if (!aChecked && !bChecked) {
              return a.user.last_name.localeCompare(b.user.last_name);
            }
            return aChecked === bChecked ? 0 : aChecked ? 1 : -1;
          })
          .map(([mitarbeiternumer, { user, workplansList }]) => {
            return (
              user && (
                <WorkPlanContainer key={mitarbeiternumer}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    flexDirection={{ xs: "column", md: "row" }}
                    gap={{ xs: 1, md: 0 }}
                    sx={{
                      opacity:
                        isStatusActiveToday(user?.checked) ||
                        isStatusActiveToday(user?.sick)
                          ? 0.5
                          : 1,
                    }}
                  >
                    <Box width={{ xs: "100%", md: "25%" }}>
                      <Monteur userList={[user]} />
                    </Box>
                    <Box width={{ xs: "100%", md: "55%" }}>
                      {workplansList
                        .sort((a, b) =>
                          getArbeitsplanTitleNoVorgang(a[0])?.localeCompare(
                            getArbeitsplanTitleNoVorgang(b[0]),
                          ),
                        )
                        .map((ap) => (
                          <Box
                            key={ap[0].vorgang}
                            display="flex"
                            flexDirection={{ xs: "column", sm: "row" }}
                            alignItems={{ xs: "flex-start", sm: "center" }}
                            justifyContent="space-between"
                            mb={{ xs: 1, sm: 0 }}
                          >
                            <Box width={{ xs: "100%", sm: "50%" }}>
                              <Typography>
                                {getArbeitsplanTitleNoVorgang(ap[0])}
                              </Typography>
                            </Box>
                            <Box width={{ xs: "100%", sm: "50%" }}>
                              <Typography>
                                {getFullHourTimeFrame(ap)}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                    </Box>
                    <Box
                      width={{ xs: "100%", md: "20%" }}
                      display="flex"
                      justifyContent={{ xs: "flex-start", md: "flex-end" }}
                    >
                      <Tooltip title="Geprüft">
                        <Checkbox
                          icon={<CheckCircleOutlineIcon color="action" />}
                          checkedIcon={
                            <CheckCircleOutlineIcon color="success" />
                          }
                          checked={isStatusActiveToday(user?.checked)}
                          onClick={(e) => {
                            if (isStatusActiveToday(user?.sick))
                              e.preventDefault();
                          }}
                          onChange={async (e) => {
                            if (isStatusActiveToday(user?.sick)) return;
                            setMitarbeiterAnsicht((prev) => {
                              if (!prev) return prev;
                              return {
                                ...prev,
                                [mitarbeiternumer]: {
                                  ...prev[mitarbeiternumer],
                                  user: {
                                    ...user,
                                    checked: e.target.checked
                                      ? new Date()
                                      : null,
                                  },
                                },
                              };
                            });
                            setUsers((prev) =>
                              prev.map((u) =>
                                u.id === user.id
                                  ? {
                                      ...u,
                                      checked: e.target.checked
                                        ? new Date()
                                        : null,
                                    }
                                  : u,
                              ),
                            );
                            await handleCheckboxChange(e, user.id);
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Zu spät">
                        <Box display="inline-flex" alignItems="center">
                          <Checkbox
                            icon={<WatchLaterIcon color="action" />}
                            checkedIcon={<WatchLaterIcon color="warning" />}
                            checked={isStatusActiveToday(user?.late)}
                            onClick={(e) => {
                              if (
                                isStatusActiveToday(user?.late) ||
                                isStatusActiveToday(user?.sick)
                              )
                                e.preventDefault();
                            }}
                            onChange={(e) => {
                              if (
                                isStatusActiveToday(user?.late) ||
                                isStatusActiveToday(user?.sick)
                              )
                                return;
                              setSelectedLateUser(user);
                              setLateMinutesValue(5);
                              setIsEditingLateMinutes(false);
                              setLateDialogOpen(true);
                            }}
                          />
                          {isStatusActiveToday(user?.late) &&
                            (user?.late_minutes || 0) > 0 && (
                              <Typography
                                variant="caption"
                                sx={{ color: "orange", ml: -0.5, mr: 1 }}
                              >
                                {(user?.late_minutes || 0) >= 60
                                  ? `${Math.floor((user?.late_minutes || 0) / 60)}h ${(user?.late_minutes || 0) % 60 > 0 ? `${(user?.late_minutes || 0) % 60}m` : ""}`
                                  : `${user?.late_minutes || 0}m`}
                              </Typography>
                            )}
                        </Box>
                      </Tooltip>
                      <Tooltip title="Krank">
                        <Checkbox
                          icon={<SickIcon color="action" />}
                          checkedIcon={<SickIcon color="error" />}
                          checked={isStatusActiveToday(user?.sick)}
                          onClick={(e) => {
                            if (isStatusActiveToday(user?.sick))
                              e.preventDefault();
                          }}
                          onChange={(e) => {
                            if (isStatusActiveToday(user?.sick)) return;
                            setSelectedSickUser(user);
                            setSickDialogOpen(true);
                          }}
                        />
                      </Tooltip>
                    </Box>
                  </Box>
                </WorkPlanContainer>
              )
            );
          })}
      {showBaustellenAnsicht &&
        baustellenAnsicht
          .sort((a, b) => {
            const aUnchecked = users
              .filter((user) =>
                a
                  .flatMap((ap) =>
                    ap.mitarbeiter?.split(",").map((x) => x?.trim()),
                  )
                  .includes(String(user.mitarbeiternummer)),
              )
              .some(
                (user) =>
                  !isStatusActiveToday(user?.checked) &&
                  !isStatusActiveToday(user?.sick),
              );

            const bUnchecked = users
              .filter((user) =>
                b
                  .flatMap((ap) =>
                    ap.mitarbeiter?.split(",").map((x) => x?.trim()),
                  )
                  .includes(String(user.mitarbeiternummer)),
              )
              .some(
                (user) =>
                  !isStatusActiveToday(user?.checked) &&
                  !isStatusActiveToday(user?.sick),
              );

            if (aUnchecked && !bUnchecked) return -1;
            if (!aUnchecked && bUnchecked) return 1;
            return getArbeitsplanTitleNoVorgang(a[0])?.localeCompare(
              getArbeitsplanTitleNoVorgang(b[0]),
            );
          })
          .map((kurzubersichtList, index) => {
            const mitarbeiter = [
              ...new Set(
                kurzubersichtList.flatMap((ap) =>
                  ap.mitarbeiter?.split(",").map((x) => x?.trim()),
                ),
              ),
            ];
            const userList = users.filter((user) =>
              mitarbeiter.includes(String(user.mitarbeiternummer)),
            );
            return (
              <WorkPlanContainer key={index}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  flexDirection={{ xs: "column", md: "row" }}
                  gap={{ xs: 1, md: 0 }}
                  sx={{
                    opacity: userList.every(
                      (user) =>
                        isStatusActiveToday(user?.checked) ||
                        isStatusActiveToday(user?.sick),
                    )
                      ? 0.5
                      : 1,
                  }}
                >
                  <Box width={{ xs: "100%", md: "25%" }}>
                    <Typography
                      sx={{ fontWeight: { xs: "bold", md: "normal" } }}
                    >
                      {getArbeitsplanTitleNoVorgang(kurzubersichtList[0])}
                    </Typography>
                  </Box>
                  <Box width={{ xs: "100%", md: "25%" }}>
                    <Typography color="textSecondary">
                      {getFullHourTimeFrame(kurzubersichtList)}
                    </Typography>
                  </Box>
                  <Box width={{ xs: "100%", md: "50%" }}>
                    {userList.map((user) => (
                      <Box
                        key={user.id}
                        display="flex"
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        justifyContent="space-between"
                        flexDirection={{ xs: "column", sm: "row" }}
                        gap={{ xs: 1, sm: 0 }}
                        mb={{ xs: 1, sm: 0 }}
                        sx={{
                          opacity:
                            isStatusActiveToday(user?.checked) ||
                            isStatusActiveToday(user?.sick)
                              ? 0.5
                              : 1,
                          borderBottom: { xs: "1px solid #eee", sm: "none" },
                          pb: { xs: 1, sm: 0 },
                        }}
                      >
                        <Box width={{ xs: "100%", sm: "auto" }}>
                          <Monteur userList={[user]} />
                        </Box>
                        <Box
                          display="flex"
                          width={{ xs: "100%", sm: "auto" }}
                          justifyContent={{ xs: "flex-start", sm: "flex-end" }}
                        >
                          <Tooltip title="Geprüft">
                            <Checkbox
                              icon={<CheckCircleOutlineIcon color="action" />}
                              checkedIcon={
                                <CheckCircleOutlineIcon color="success" />
                              }
                              checked={isStatusActiveToday(user?.checked)}
                              onClick={(e) => {
                                if (isStatusActiveToday(user?.sick))
                                  e.preventDefault();
                              }}
                              onChange={async (e) => {
                                if (isStatusActiveToday(user?.sick)) return;
                                setUsers((prev) =>
                                  prev.map((u) =>
                                    u.id === user.id
                                      ? {
                                          ...u,
                                          checked: e.target.checked
                                            ? new Date()
                                            : null,
                                        }
                                      : u,
                                  ),
                                );
                                await handleCheckboxChange(e, user.id);
                              }}
                            />
                          </Tooltip>
                          <Tooltip title="Zu spät">
                            <Box display="inline-flex" alignItems="center">
                              <Checkbox
                                icon={<WatchLaterIcon color="action" />}
                                checkedIcon={<WatchLaterIcon color="warning" />}
                                checked={isStatusActiveToday(user?.late)}
                                onClick={(e) => {
                                  if (
                                    isStatusActiveToday(user?.late) ||
                                    isStatusActiveToday(user?.sick)
                                  )
                                    e.preventDefault();
                                }}
                                onChange={(e) => {
                                  if (
                                    isStatusActiveToday(user?.late) ||
                                    isStatusActiveToday(user?.sick)
                                  )
                                    return;
                                  setSelectedLateUser(user);
                                  setLateMinutesValue(5);
                                  setIsEditingLateMinutes(false);
                                  setLateDialogOpen(true);
                                }}
                              />
                              {isStatusActiveToday(user?.late) &&
                                (user?.late_minutes || 0) > 0 && (
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "orange", ml: -0.5, mr: 1 }}
                                  >
                                    {(user?.late_minutes || 0) >= 60
                                      ? `${Math.floor((user?.late_minutes || 0) / 60)}h ${(user?.late_minutes || 0) % 60 > 0 ? `${(user?.late_minutes || 0) % 60}m` : ""}`
                                      : `${user?.late_minutes || 0}m`}
                                  </Typography>
                                )}
                            </Box>
                          </Tooltip>
                          <Tooltip title="Krank">
                            <Checkbox
                              icon={<SickIcon color="action" />}
                              checkedIcon={<SickIcon color="error" />}
                              checked={isStatusActiveToday(user?.sick)}
                              onClick={(e) => {
                                if (isStatusActiveToday(user?.sick))
                                  e.preventDefault();
                              }}
                              onChange={(e) => {
                                if (isStatusActiveToday(user?.sick)) return;
                                setSelectedSickUser(user);
                                setSickDialogOpen(true);
                              }}
                            />
                          </Tooltip>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </WorkPlanContainer>
            );
          })}
      {showBaustellenAnsicht && abwesend && abwesend.length > 0 && (
        <WorkPlanContainer key={999}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            flexDirection={{ xs: "column", md: "row" }}
            gap={{ xs: 1, md: 0 }}
          >
            <Box width={{ xs: "100%", md: "50%" }}>
              <Typography sx={{ fontWeight: { xs: "bold", md: "normal" } }}>
                Abwesend
              </Typography>
            </Box>
            <Box width={{ xs: "100%", md: "50%" }}>
              <div className="monteurList">
                <Box display="flex" alignItems="flex-start">
                  <PersonIcon />
                  <Box marginLeft={0}>
                    {abwesend.map((abwesendMitarbeiter, index) => (
                      <div key={index}>
                        <strong key={index}>
                          {abwesendMitarbeiter.name}
                          <br />
                        </strong>
                        {/* {abwesendMitarbeiter.dateRange &&
                abwesendMitarbeiter.dateRange != "" && (
                <Typography>
                  {abwesendMitarbeiter.dateRange}
                </Typography>
                )} */}
                      </div>
                    ))}
                  </Box>
                </Box>
              </div>
            </Box>
          </Box>
        </WorkPlanContainer>
      )}

      <Dialog
        open={lateDialogOpen}
        onClose={() => setLateDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          sx={{ textAlign: "center", fontSize: { xs: "1rem", md: "1.25rem" } }}
        >
          Verspätung auswählen
          <br />
          <Typography variant="caption" color="textSecondary">
            {selectedLateUser?.nameAdresse || selectedLateUser?.first_name}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ mt: 3, mb: 1, gap: { xs: 3, md: 2 } }}
          >
            <Button
              variant="outlined"
              size="large"
              sx={{
                minWidth: { xs: "64px", md: "auto" },
                height: { xs: "64px", md: "auto" },
                borderRadius: 2,
              }}
              onClick={() =>
                setLateMinutesValue((prev) => Math.max(0, prev - 5))
              }
              disabled={lateMinutesValue <= 0}
            >
              -
            </Button>
            {isEditingLateMinutes ? (
              <TextField
                type="number"
                value={lateMinutesValue}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  setLateMinutesValue(isNaN(val) ? 0 : val);
                }}
                onBlur={() => setIsEditingLateMinutes(false)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setIsEditingLateMinutes(false);
                  }
                }}
                autoFocus
                inputProps={{
                  min: 0,
                  max: 480,
                  style: {
                    textAlign: "center",
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                  },
                }}
                sx={{ width: "120px" }}
                variant="standard"
              />
            ) : (
              <Typography
                variant="h4"
                sx={{
                  minWidth: "120px",
                  textAlign: "center",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
                onClick={() => setIsEditingLateMinutes(true)}
              >
                {lateMinutesValue >= 60
                  ? `${Math.floor(lateMinutesValue / 60)}h ${lateMinutesValue % 60 > 0 ? `${lateMinutesValue % 60}m` : ""}`
                  : `${lateMinutesValue}m`}
              </Typography>
            )}
            <Button
              variant="outlined"
              size="large"
              sx={{
                minWidth: { xs: "64px", md: "auto" },
                height: { xs: "64px", md: "auto" },
                borderRadius: 2,
              }}
              onClick={() =>
                setLateMinutesValue((prev) => Math.min(480, prev + 5))
              }
              disabled={lateMinutesValue >= 480}
            >
              +
            </Button>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            justifyContent: "center",
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Button
            fullWidth
            sx={{ mt: { xs: 1, sm: 0 } }}
            size="large"
            onClick={() => setLateDialogOpen(false)}
          >
            Abbrechen
          </Button>
          <Button
            fullWidth
            size="large"
            variant="contained"
            color="primary"
            onClick={async () => {
              if (selectedLateUser) {
                const now = new Date();
                setUsers((prev) =>
                  prev.map((u) =>
                    u.id === selectedLateUser.id
                      ? { ...u, late: now, late_minutes: lateMinutesValue }
                      : u,
                  ),
                );
                if (mitarbeiterAnsicht) {
                  setMitarbeiterAnsicht((prev) => {
                    if (!prev) return prev;
                    const newAnsicht = { ...prev };
                    const key = Object.keys(newAnsicht).find(
                      (k) => newAnsicht[k].user.id === selectedLateUser.id,
                    );
                    if (key) {
                      newAnsicht[key].user.late = now;
                      newAnsicht[key].user.late_minutes = lateMinutesValue;
                    }
                    return newAnsicht;
                  });
                }
                await handleLateChange(
                  selectedLateUser.id,
                  true,
                  lateMinutesValue,
                );
              }
              setLateDialogOpen(false);
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={sickDialogOpen}
        onClose={() => setSickDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ textAlign: "center" }}>
          Krankmeldung bestätigen
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1, textAlign: "center" }}>
            Möchten Sie{" "}
            <strong>
              {selectedSickUser?.nameAdresse || selectedSickUser?.first_name}
            </strong>{" "}
            wirklich als "Krank" eintragen?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            justifyContent: "center",
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Button
            fullWidth
            sx={{ mt: { xs: 1, sm: 0 } }}
            size="large"
            onClick={() => setSickDialogOpen(false)}
          >
            Abbrechen
          </Button>
          <Button
            fullWidth
            size="large"
            variant="contained"
            color="error"
            onClick={async () => {
              if (selectedSickUser) {
                const now = new Date();
                setUsers((prev) =>
                  prev.map((u) =>
                    u.id === selectedSickUser.id ? { ...u, sick: now } : u,
                  ),
                );
                if (mitarbeiterAnsicht) {
                  setMitarbeiterAnsicht((prev) => {
                    if (!prev) return prev;
                    const newAnsicht = { ...prev };
                    const key = Object.keys(newAnsicht).find(
                      (k) => newAnsicht[k].user.id === selectedSickUser.id,
                    );
                    if (key) {
                      newAnsicht[key].user.sick = now;
                    }
                    return newAnsicht;
                  });
                }
                await handleSickChange(
                  {
                    target: { checked: true },
                  } as React.ChangeEvent<HTMLInputElement>,
                  selectedSickUser.id,
                );
              }
              setSickDialogOpen(false);
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </PageWrapper>
  );
};
